import { AxiosInstance, AxiosRequestConfig } from 'axios';

import { ACCESS_KEY } from './auth';

/** Фоновые задачи scheduler — через api-gateway SNEG-Delivery (тот же адрес, что и socket API). */
const API_URL = process.env.REACT_APP_SOCKET_API_ENDPOINT;

export type TaskStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'canceling' | 'canceled';

export type TaskLogLevel = 'info' | 'warn' | 'error';

export type TaskLogEntry = {
  /** Ключ keyset-пагинации журнала. */
  id?: number;
  created_at: string;
  /** Индекс элемента; -1 — запись про задачу целиком. */
  item_index: number;
  level: TaskLogLevel;
  message: string;
};

/** Страница журнала задачи: GET /task/logs, от новых записей к старым. */
export type TaskLogPage = {
  data: TaskLogEntry[];
  /** Записей под фильтром уровня во всём журнале. */
  total: number;
  /** Записей каждого уровня во всём журнале, без учёта фильтра. */
  levels: Partial<Record<TaskLogLevel, number>> & Record<string, number>;
  /** Курсор следующей (более старой) страницы; 0 — старше ничего нет. */
  next_before_id: number;
};

/** Счётчики импорта; на разных типах задач набор ключей разный. */
export type TaskStats = Record<string, number | string | undefined>;

export type Task = {
  id: number;
  type: string;
  status: TaskStatus;
  source?: string;
  created_by?: string;
  created_at?: string;
  started_at?: string;
  finished_at?: string;
  updated_at?: string;
  /** Уже обработано; также точка возобновления после рестарта. */
  processed?: number;
  total?: number;
  failed?: number;
  /** processed / total, 0..1 */
  progress?: number;
  error?: string;
  options?: Record<string, unknown>;
  stats?: TaskStats;
  logs?: TaskLogEntry[];
  /** Только в ответе импорта: пропущенные строки. */
  skipped?: number;
  /** Только в ответе импорта: замечания к файлу. */
  warnings?: string[];
};

export type TasksFilter = {
  /** Например sku.import или outlet.routes */
  type?: string;
  status?: TaskStatus;
};

export type TasksPayload = {
  /** Максимум 500. */
  limit: number;
  offset: number;
  filter?: TasksFilter;
};

export type TasksResponse = {
  data: Task[];
  total: number;
};

export type TaskGetPayload = {
  id: number;
  /** Хвост лога, по умолчанию 200, максимум 1000; 0 — без лога. */
  logs?: number;
  /** 1 — вернуть прикреплённые items (исходный файл). */
  items?: '1';
};

export type TaskLogsPayload = {
  id: number;
  /** Только эти уровни; пусто — все. */
  levels?: TaskLogLevel[];
  /** Курсор: next_before_id предыдущей страницы; не задан — самые свежие записи. */
  before_id?: number;
  /** Размер страницы, по умолчанию 50, максимум 500. */
  limit?: number;
};

export type TaskCancelResponse = {
  id: number;
  status: string;
};

/** Разделитель CSV для POST /import/*. Пустое / auto — автоопределение на шлюзе. */
export type ImportCsvDelimiter = '' | 'auto' | 'comma' | 'semicolon' | 'tab';

type ImportCsvBasePayload = {
  file: File;
  /** Только отчёт, без записи в SNEG. */
  dry_run?: boolean;
  /** Первая строка CSV — заголовок (по умолчанию true). */
  header?: boolean;
  delim?: ImportCsvDelimiter;
};

export type ImportSkuPayload = ImportCsvBasePayload & {
  /** Оставлять только SKU с точным совпадением кода. */
  exact?: boolean;
  /** Обновлять number_in_package даже если у записи не хватает обязательных полей. */
  force_update?: boolean;
};

export type ImportOutletRoutesPayload = ImportCsvBasePayload & {
  /** Деактивировать текущие привязки ТТ к маршрутам перед созданием новой (по умолчанию true). */
  deactivate_existing?: boolean;
};

const appendBool = (form: FormData, key: string, value?: boolean) => {
  if (value === undefined) {
    return;
  }
  form.append(key, value ? 'true' : 'false');
};

const appendDelim = (form: FormData, delim?: ImportCsvDelimiter) => {
  if (delim && delim !== 'auto') {
    form.append('delim', delim);
  }
};

/** multipart/form-data: токен без Bearer, Content-Type с boundary проставит браузер. */
const multipartConfig: AxiosRequestConfig = {
  baseURL: '/',
  withCredentials: false,
  transformResponse: (data) => {
    try {
      return JSON.parse(data);
    } catch (e) {
      return data;
    }
  },
  transformRequest: (data, headers) => {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) {
      headers['Authorization'] = token;
    }
    if (typeof headers.delete === 'function') {
      headers.delete('Content-Type');
    } else {
      delete headers['Content-Type'];
    }
    return data;
  },
};

const tasks = (instance: AxiosInstance, config: AxiosRequestConfig) => ({
  getTasks: ({ limit, offset, filter = {} }: TasksPayload) =>
    instance.get<ApiResponse<TasksResponse>>(`${API_URL}tasks`, {
      ...config,
      baseURL: '/',
      params: { limit, offset, ...filter },
    }),
  getTask: ({ id, logs, items }: TaskGetPayload) =>
    instance.get<ApiResponse<Task>>(`${API_URL}task`, {
      ...config,
      baseURL: '/',
      params: {
        id,
        ...(logs != null ? { logs } : {}),
        ...(items ? { items } : {}),
      },
    }),
  getTaskLogs: ({ id, levels, before_id, limit }: TaskLogsPayload) =>
    instance.get<ApiResponse<TaskLogPage>>(`${API_URL}task/logs`, {
      ...config,
      baseURL: '/',
      params: {
        id,
        ...(levels && levels.length > 0 ? { level: levels.join(',') } : {}),
        ...(before_id ? { before_id } : {}),
        ...(limit ? { limit } : {}),
      },
    }),
  cancelTask: (id: number) =>
    instance.post<ApiResponse<TaskCancelResponse>>(`${API_URL}task/cancel`, null, {
      ...config,
      baseURL: '/',
      params: { id },
    }),
  importSku: ({ file, dry_run, exact, force_update, header, delim }: ImportSkuPayload) => {
    const form = new FormData();
    form.append('file', file);
    appendBool(form, 'dry_run', dry_run);
    appendBool(form, 'exact', exact);
    appendBool(form, 'force_update', force_update);
    appendBool(form, 'header', header);
    appendDelim(form, delim);
    return instance.post<ApiResponse<Task>>(`${API_URL}import/sku`, form, multipartConfig);
  },
  importOutletRoutes: ({ file, dry_run, deactivate_existing, header, delim }: ImportOutletRoutesPayload) => {
    const form = new FormData();
    form.append('file', file);
    appendBool(form, 'dry_run', dry_run);
    appendBool(form, 'deactivate_existing', deactivate_existing);
    appendBool(form, 'header', header);
    appendDelim(form, delim);
    return instance.post<ApiResponse<Task>>(`${API_URL}import/outlet_routes`, form, multipartConfig);
  },
});

export default tasks;
